/* Small styled form primitives for the expense module. The shared @andavo/web-ui
   kit ships display components (Button/Card/Table/Badge/Kpi) but no form inputs, so
   these local primitives fill the gap using the same design tokens (border-subtle,
   surface-midground, content-*, brand focus ring). Kept local — do NOT push these
   into the drift-guarded design system without the andavo-dev source. */
import { cn } from '@andavo/web-ui';
import type {
  InputHTMLAttributes,
  LabelHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from 'react';

const controlClass =
  'w-full rounded-md border border-border-subtle bg-surface-background px-3 py-2 font-inter text-content-primary text-sm outline-none transition-colors placeholder:text-content-secondary focus:border-border-action focus:ring-2 focus:ring-border-action/30 disabled:cursor-not-allowed disabled:opacity-60';

export function Input({
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(controlClass, className)} {...props} />;
}

export function Select({
  className,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cn(controlClass, 'pe-8', className)} {...props}>
      {children}
    </select>
  );
}

export function Label({
  className,
  children,
  ...props
}: LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label
      className={cn(
        'font-inter font-medium text-content-secondary text-xs uppercase tracking-wide',
        className,
      )}
      {...props}
    >
      {children}
    </label>
  );
}

/** A labelled field: label above its control, with an optional hint/error line. */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  className,
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  error?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error ? (
        <span className="font-inter text-content-danger text-xs">{error}</span>
      ) : hint ? (
        <span className="font-inter text-content-secondary text-xs">
          {hint}
        </span>
      ) : null}
    </div>
  );
}
